import React, { useState, useEffect, useCallback } from 'react';
import logger from '../lib/logger';
import { 
  Bot,
  Play,
  Pause,
  Square,
  Trash2,
  Plus,
  RefreshCw,
  Settings,
  Users,
  Shield,
  Clock,
  Zap,
  MessageSquare,
  Youtube,
  Instagram,
  CheckCircle,
  XCircle,
  AlertCircle,
  Send,
  Loader2,
  RotateCcw,
  Film,
  Eye,
  ChevronDown,
  ChevronUp,
  Sparkles,
  Calendar,
  Copy
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useAuth } from '@/lib/AuthContext';

// TikTok icon
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
  </svg>
);

const PlatformIcon = ({ platform, className = "w-4 h-4" }) => {
  if (platform.includes('youtube')) return <Youtube className={className} />;
  if (platform.includes('tiktok')) return <TikTokIcon className={className} />;
  if (platform.includes('instagram')) return <Instagram className={className} />;
  return <Play className={className} />;
};

const StatusBadge = ({ status }) => {
  const config = {
    active: { color: 'bg-green-500/20 text-green-400', icon: CheckCircle, label: 'Ativo' },
    paused: { color: 'bg-yellow-500/20 text-yellow-400', icon: Pause, label: 'Pausado' },
    stopped: { color: 'bg-gray-500/20 text-gray-400', icon: Square, label: 'Parado' },
    error: { color: 'bg-red-500/20 text-red-400', icon: XCircle, label: 'Erro' }
  };

  const cfg = config[status] || config.stopped;
  const Icon = cfg.icon;

  return (
    <Badge className={cfg.color}>
      <Icon className="w-3 h-3 mr-1" />
      {cfg.label}
    </Badge>
  );
};

const queueLabel = (s) => ({ pending: 'Pendentes', in_progress: 'Em execução', completed: 'Concluídas', failed: 'Falharam' }[s] || s);

const TaskStatusDot = ({ status }) => {
  const color = { pending: 'bg-yellow-400', in_progress: 'bg-blue-400', completed: 'bg-green-400', failed: 'bg-red-400' }[status] || 'bg-gray-400';
  return <span className={`inline-block w-2 h-2 rounded-full mr-1 ${color}`} />;
};

const taskTypeLabel = (t) => {
  if (!t) return 'Tarefa';
  if (t.startsWith('post_to_')) return 'Publicar clip';
  return { fetch_analytics: 'Coletar analytics', check_account_status: 'Verificar conta', search_videos: 'Descobrir vídeos', download_video: 'Baixar vídeo (PC)' }[t] || t;
};

const AgentTaskRow = ({ task }) => {
  const statusCfg = {
    pending: { label: 'Pendente', cls: 'bg-yellow-500/20 text-yellow-400' },
    in_progress: { label: 'Em execução', cls: 'bg-blue-500/20 text-blue-400' },
    completed: { label: 'Concluída', cls: 'bg-green-500/20 text-green-400' },
    failed: { label: 'Falhou', cls: 'bg-red-500/20 text-red-400' },
  }[task.status] || { label: task.status, cls: 'bg-gray-500/20 text-gray-400' };
  const when = task.created_at ? new Date(task.created_at).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '';
  return (
    <Card className="glass-card" data-testid={`agent-task-${task.id}`}>
      <CardContent className="py-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          {task.platform ? <PlatformIcon platform={task.platform} className="w-4 h-4 shrink-0" /> : <Bot className="w-4 h-4 shrink-0 text-primary" />}
          <div className="min-w-0">
            <p className="text-sm font-medium truncate">
              {taskTypeLabel(task.task_type)}
              {task.account_label ? <span className="text-muted-foreground font-normal"> · {task.account_label}</span> : null}
            </p>
            <p className="text-xs text-muted-foreground truncate">
              {task.clip_title ? `${task.clip_title} · ` : ''}{when}
              {task.error_message ? <span className="text-red-400"> · {task.error_message}</span> : null}
            </p>
          </div>
        </div>
        <Badge className={`${statusCfg.cls} shrink-0`}>{statusCfg.label}</Badge>
      </CardContent>
    </Card>
  );
};

// Vault account picker (references only - never shows credentials)
const AccountPicker = ({ accounts, selected, onToggle }) => {
  if (!accounts || accounts.length === 0) {
    return (
      <div className="text-sm text-muted-foreground bg-muted/30 rounded-lg p-3">
        Nenhuma conta no Cofre ainda. Adicione contas em <span className="font-medium">Cofre Seguro</span> para usá-las aqui.
      </div>
    );
  }
  return (
    <div className="space-y-2 max-h-40 overflow-y-auto pr-1">
      {accounts.map((acc) => {
        const isSel = selected.includes(acc.id);
        return (
          <button
            type="button"
            key={acc.id}
            onClick={() => onToggle(acc.id)}
            className={`w-full flex items-center gap-3 rounded-lg border p-2 text-left transition-colors ${isSel ? 'border-primary bg-primary/10' : 'border-border hover:bg-muted/50'}`}
            data-testid={`autopilot-account-option-${acc.id}`}
          >
            <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center shrink-0">
              <PlatformIcon platform={acc.platform} className="w-4 h-4" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium truncate">{acc.account_label || acc.username_hint}</p>
              <p className="text-xs text-muted-foreground capitalize">{acc.platform} · {acc.username_hint}</p>
            </div>
            <div className={`w-5 h-5 rounded-full border flex items-center justify-center shrink-0 ${isSel ? 'bg-primary border-primary' : 'border-muted-foreground/40'}`}>
              {isSel && <CheckCircle className="w-4 h-4 text-primary-foreground" />}
            </div>
          </button>
        );
      })}
      <p className="text-xs text-muted-foreground pt-1">
        <Shield className="w-3 h-3 inline mr-1 text-green-500" />
        O app guarda apenas a referência da conta. As credenciais permanecem criptografadas e só são usadas pelo Agente na execução.
      </p>
    </div>
  );
};

// Clip Card Component
const ClipCard = ({ clip }) => {
  const [expanded, setExpanded] = useState(false);
  
  const statusConfig = {
    scheduled: { color: 'bg-blue-500/20 text-blue-400', label: 'Agendado' },
    processing: { color: 'bg-yellow-500/20 text-yellow-400', label: 'Processando' },
    published: { color: 'bg-green-500/20 text-green-400', label: 'Publicado' },
    failed: { color: 'bg-red-500/20 text-red-400', label: 'Falhou' },
    viral_detected: { color: 'bg-purple-500/20 text-purple-400', label: 'Viral Detectado' }
  };

  const status = statusConfig[clip.status] || statusConfig.processing;

  const copyCaption = () => {
    const caption = clip.viral_moment?.caption || '';
    const hashtags = clip.viral_moment?.hashtags?.join(' ') || '';
    navigator.clipboard.writeText(`${caption} ${hashtags}`);
    toast.success('Caption copiada!');
  };

  return (
    <Card className="glass-card border-l-4" style={{ borderLeftColor: clip.viral_moment?.viral_score >= 80 ? '#22c55e' : clip.viral_moment?.viral_score >= 60 ? '#eab308' : '#6b7280' }}>
      <CardContent className="py-3">
        <div className="flex justify-between items-start mb-2">
          <div className="flex-1">
            <p className="font-medium text-sm truncate">
              {clip.source_video?.title?.substring(0, 50) || 'Clip'}...
            </p>
            <p className="text-xs text-muted-foreground">
              {clip.source_video?.channel}
            </p>
          </div>
          <Badge className={status.color}>{status.label}</Badge>
        </div>

        {clip.viral_moment && (
          <div className="flex items-center gap-3 mb-2">
            <div className="flex items-center gap-1">
              <Sparkles className="w-4 h-4 text-primary" />
              <span className="text-sm font-bold">{clip.viral_moment.viral_score}</span>
            </div>
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <Clock className="w-3 h-3" />
              {Math.round(clip.viral_moment.duration)}s
            </div>
            <Badge variant="outline" className="text-xs">
              {clip.viral_moment.emotion}
            </Badge>
          </div>
        )}

        <div className="flex gap-1 mb-2">
          {clip.platforms?.map(platform => (
            <Badge key={platform} variant="outline" className="text-xs flex items-center gap-1">
              <PlatformIcon platform={platform} className="w-3 h-3" />
            </Badge>
          ))}
        </div>

        {expanded && clip.viral_moment && (
          <div className="mt-3 space-y-2 border-t pt-3">
            <div>
              <p className="text-xs text-muted-foreground mb-1">Caption sugerida:</p>
              <p className="text-sm">{clip.viral_moment.caption}</p>
            </div>
            <div className="flex flex-wrap gap-1">
              {clip.viral_moment.hashtags?.map(tag => (
                <Badge key={tag} variant="secondary" className="text-xs">{tag}</Badge>
              ))}
            </div>
            <Button size="sm" variant="outline" onClick={copyCaption}>
              <Copy className="w-3 h-3 mr-1" />
              Copiar Caption
            </Button>
          </div>
        )}

        <div className="flex justify-between items-center mt-2">
          <div className="text-xs text-muted-foreground flex items-center gap-1">
            <Calendar className="w-3 h-3" />
            {clip.scheduled_time ? new Date(clip.scheduled_time).toLocaleString('pt-BR', { 
              day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' 
            }) : '-'}
          </div>
          <Button 
            size="sm" 
            variant="ghost" 
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const AutopilotCard = ({ autopilot, accountsMap, onPause, onResume, onStop, onDelete, onRunNow, onViewClips, onEditAccounts }) => {
  const [loading, setLoading] = useState(false);

  const handleAction = async (action) => {
    setLoading(true);
    try {
      await action();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="glass-card" data-testid={`autopilot-${autopilot.id}`}>
      <CardHeader className="pb-2">
        <div className="flex justify-between items-start">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Bot className="w-5 h-5 text-primary" />
              {autopilot.content_theme || 'Autopilot'}
            </CardTitle>
            <CardDescription className="mt-1">
              {autopilot.clips_per_day} clips/dia • {autopilot.content_language}
            </CardDescription>
          </div>
          <StatusBadge status={autopilot.status} />
        </div>
      </CardHeader>
      
      <CardContent className="space-y-3">
        {/* Platforms */}
        <div className="flex gap-2">
          {autopilot.platforms?.map(platform => (
            <Badge key={platform} variant="outline" className="flex items-center gap-1">
              <PlatformIcon platform={platform} className="w-3 h-3" />
              {platform.replace('_', ' ')}
            </Badge>
          ))}
        </div>

        {/* Vault accounts (references only) */}
        <div className="bg-muted/30 rounded-lg p-2 space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-xs text-muted-foreground flex items-center gap-1">
              <Shield className="w-3 h-3 text-green-500" /> Contas do Cofre
            </p>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-xs"
              onClick={() => onEditAccounts(autopilot)}
              data-testid={`autopilot-edit-accounts-${autopilot.id}`}
            >
              <Settings className="w-3 h-3 mr-1" /> Configurar
            </Button>
          </div>
          <div className="flex flex-wrap gap-1">
            {(autopilot.account_ids || []).length === 0 ? (
              <span className="text-xs text-muted-foreground">Nenhuma conta selecionada — usa qualquer conta da plataforma</span>
            ) : (
              (autopilot.account_ids || []).map((id) => {
                const acc = accountsMap?.[id];
                return (
                  <Badge key={id} variant="secondary" className="flex items-center gap-1 text-xs">
                    <PlatformIcon platform={acc?.platform || ''} className="w-3 h-3" />
                    {acc?.account_label || acc?.username_hint || 'Conta'}
                  </Badge>
                );
              })
            )}
          </div>
          {autopilot.custom_instructions ? (
            <div className="flex items-start gap-1 text-xs text-muted-foreground border-t border-border/50 pt-2">
              <MessageSquare className="w-3 h-3 mt-0.5 shrink-0" />
              <span className="line-clamp-2">{autopilot.custom_instructions}</span>
            </div>
          ) : null}
        </div>

        {/* Original command */}
        <div className="bg-muted/30 rounded-lg p-2">
          <p className="text-xs text-muted-foreground mb-1">Comando:</p>
          <p className="text-sm line-clamp-2">{autopilot.original_command}</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-2 text-center">
          <div className="bg-muted/30 rounded p-2">
            <p className="text-xs text-muted-foreground">Hoje</p>
            <p className="font-bold">{autopilot.clips_published_today || 0}</p>
          </div>
          <div className="bg-muted/30 rounded p-2">
            <p className="text-xs text-muted-foreground">Total</p>
            <p className="font-bold">{autopilot.total_clips_published || 0}</p>
          </div>
          <div className="bg-muted/30 rounded p-2">
            <p className="text-xs text-muted-foreground">Próximo</p>
            <p className="text-xs font-medium">
              {autopilot.next_run 
                ? new Date(autopilot.next_run).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
                : '-'
              }
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-2 pt-2">
          {autopilot.status === 'active' ? (
            <Button 
              variant="outline" 
              size="sm" 
              className="flex-1"
              onClick={() => handleAction(() => onPause(autopilot.id))}
              disabled={loading}
            >
              <Pause className="w-4 h-4 mr-1" />
              Pausar
            </Button>
          ) : autopilot.status === 'paused' ? (
            <Button 
              variant="outline" 
              size="sm" 
              className="flex-1"
              onClick={() => handleAction(() => onResume(autopilot.id))}
              disabled={loading}
            >
              <Play className="w-4 h-4 mr-1" />
              Retomar
            </Button>
          ) : null}
          
          {autopilot.status === 'active' && (
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => handleAction(() => onRunNow(autopilot.id))}
              disabled={loading}
            >
              <RotateCcw className="w-4 h-4" />
            </Button>
          )}
          
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => onViewClips(autopilot.id)}
            disabled={loading}
          >
            <Film className="w-4 h-4" />
          </Button>
          
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => onStop(autopilot.id)}
            disabled={loading}
          >
            <Square className="w-4 h-4" />
          </Button>
          
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => onDelete(autopilot.id)}
            disabled={loading}
          >
            <Trash2 className="w-4 h-4 text-destructive" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default function Autopilot() {
  const { user } = useAuth();
  const isDeveloper = user?.is_developer || user?.plan === 'developer';
  
  const [loading, setLoading] = useState(true);
  const [autopilots, setAutopilots] = useState([]);
  const [status, setStatus] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [showGrant, setShowGrant] = useState(false);
  const [showClips, setShowClips] = useState(false);
  const [selectedAutopilotId, setSelectedAutopilotId] = useState(null);
  const [clips, setClips] = useState([]);
  const [loadingClips, setLoadingClips] = useState(false);
  const [command, setCommand] = useState('');
  const [creating, setCreating] = useState(false);
  const [createInstructions, setCreateInstructions] = useState('');
  const [vaultAccounts, setVaultAccounts] = useState([]);
  const [selectedAccountIds, setSelectedAccountIds] = useState([]);
  const [showAccounts, setShowAccounts] = useState(false);
  const [accountsEditId, setAccountsEditId] = useState(null);
  const [editAccountIds, setEditAccountIds] = useState([]);
  const [editInstructions, setEditInstructions] = useState('');
  const [editCommand, setEditCommand] = useState('');
  const [editLanguage, setEditLanguage] = useState('pt-BR');
  const [savingAccounts, setSavingAccounts] = useState(false);
  const [agentQueue, setAgentQueue] = useState([]);
  const [queueCounts, setQueueCounts] = useState({});
  const [loadingQueue, setLoadingQueue] = useState(false);
  
  // Grant permission form
  const [grantForm, setGrantForm] = useState({
    email: '',
    maxAutopilots: 3,
    expiresDays: ''
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Fetch autopilots
      const autopilotRes = await api.get('/autopilot/list');
      setAutopilots(autopilotRes.data.autopilots || []);

      // Fetch vault accounts (metadata only - no credentials)
      try {
        const vaultRes = await api.get('/vault/list');
        setVaultAccounts(vaultRes.data.credentials || []);
      } catch (e) {
        // vault may be empty or unavailable
      }
      
      // If developer, fetch status and permissions
      if (isDeveloper) {
        try {
          const statusRes = await api.get('/autopilot/status');
          setStatus(statusRes.data);
          
          const permsRes = await api.get('/autopilot/permissions');
          setPermissions(permsRes.data.permissions || []);
        } catch (e) {
          // Developer permissions not available - this is expected for non-developers
        }
      }
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Você não tem permissão para usar o Autopilot');
      } else {
        logger.error('Failed to fetch autopilot data:', error);
      }
    } finally {
      setLoading(false);
    }
  }, [isDeveloper]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const fetchAgentQueue = useCallback(async () => {
    try {
      setLoadingQueue(true);
      const res = await api.get('/autopilot/agent-queue');
      setAgentQueue(res.data.tasks || []);
      setQueueCounts(res.data.counts || {});
    } catch (e) {
      logger.error('Failed to fetch agent queue:', e);
    } finally {
      setLoadingQueue(false);
    }
  }, []);

  useEffect(() => {
    fetchAgentQueue();
  }, [fetchAgentQueue]);

  const handleCreateAutopilot = async () => {
    if (!command.trim()) {
      toast.error('Digite um comando para o autopilot');
      return;
    }

    setCreating(true);
    try {
      const res = await api.post('/autopilot/create', { command, account_ids: selectedAccountIds, custom_instructions: createInstructions });
      toast.success('Autopilot criado com sucesso!');
      setShowCreate(false);
      setCommand('');
      setSelectedAccountIds([]);
      setCreateInstructions('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar autopilot');
    } finally {
      setCreating(false);
    }
  };

  const toggleCreateAccount = (id) => {
    setSelectedAccountIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const openEditAccounts = (autopilot) => {
    setAccountsEditId(autopilot.id);
    setEditAccountIds(autopilot.account_ids || []);
    setEditInstructions(autopilot.custom_instructions || '');
    setEditCommand(autopilot.original_command || '');
    setEditLanguage(autopilot.content_language || 'pt-BR');
    setShowAccounts(true);
  };

  const toggleEditAccount = (id) => {
    setEditAccountIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleSaveAccounts = async () => {
    if (!accountsEditId) return;
    setSavingAccounts(true);
    try {
      await api.post(`/autopilot/${accountsEditId}/config`, { account_ids: editAccountIds, custom_instructions: editInstructions, command: editCommand, content_language: editLanguage });
      toast.success('Configurações atualizadas');
      setShowAccounts(false);
      setAccountsEditId(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar configurações');
    } finally {
      setSavingAccounts(false);
    }
  };

  const handlePause = async (id) => {
    try {
      await api.post(`/autopilot/${id}/pause`);
      toast.success('Autopilot pausado');
      fetchData();
    } catch (error) {
      toast.error('Erro ao pausar autopilot');
    }
  };

  const handleResume = async (id) => {
    try {
      await api.post(`/autopilot/${id}/resume`);
      toast.success('Autopilot retomado');
      fetchData();
    } catch (error) {
      toast.error('Erro ao retomar autopilot');
    }
  };

  const handleStop = async (id) => {
    try {
      await api.post(`/autopilot/${id}/stop`);
      toast.success('Autopilot parado');
      fetchData();
    } catch (error) {
      toast.error('Erro ao parar autopilot');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Tem certeza que deseja excluir este autopilot?')) return;
    
    try {
      await api.delete(`/autopilot/${id}`);
      toast.success('Autopilot excluído');
      fetchData();
    } catch (error) {
      toast.error('Erro ao excluir autopilot');
    }
  };

  const handleRunNow = async (id) => {
    try {
      const res = await api.post(`/autopilot/${id}/run-now`);
      toast.success(`Ciclo executado: ${res.data.results?.clips_scheduled || 0} clips agendados`);
      fetchData();
    } catch (error) {
      toast.error('Erro ao executar ciclo');
    }
  };

  const handleViewClips = async (id) => {
    setSelectedAutopilotId(id);
    setLoadingClips(true);
    setShowClips(true);
    
    try {
      const res = await api.get(`/autopilot/${id}/clips`);
      setClips(res.data.clips || []);
    } catch (error) {
      toast.error('Erro ao carregar clips');
      setClips([]);
    } finally {
      setLoadingClips(false);
    }
  };

  const handleStartRunner = async () => {
    try {
      await api.post('/autopilot/runner/start');
      toast.success('Runner iniciado');
      fetchData();
    } catch (error) {
      toast.error('Erro ao iniciar runner');
    }
  };

  const handleStopRunner = async () => {
    try {
      await api.post('/autopilot/runner/stop');
      toast.success('Runner parado');
      fetchData();
    } catch (error) {
      toast.error('Erro ao parar runner');
    }
  };

  const handleGrantPermission = async () => {
    if (!grantForm.email) {
      toast.error('Digite o email do usuário');
      return;
    }

    try {
      await api.post('/autopilot/permissions/grant', {
        user_email: grantForm.email,
        max_autopilots: grantForm.maxAutopilots,
        expires_days: grantForm.expiresDays ? parseInt(grantForm.expiresDays) : null
      });
      toast.success(`Permissão concedida para ${grantForm.email}`);
      setShowGrant(false);
      setGrantForm({ email: '', maxAutopilots: 3, expiresDays: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao conceder permissão');
    }
  };

  const handleRevokePermission = async (email) => {
    if (!confirm(`Revogar permissão de ${email}?`)) return;
    
    try {
      await api.delete(`/autopilot/permissions/${email}`);
      toast.success('Permissão revogada');
      fetchData();
    } catch (error) {
      toast.error('Erro ao revogar permissão');
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-32" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map(i => <Skeleton key={i} className="h-64" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Bot className="w-6 h-6 text-primary" />
            Autopilot AI
          </h1>
          <p className="text-muted-foreground">
            Automação completa com comandos em linguagem natural
          </p>
        </div>
        <div className="flex gap-2">
          {isDeveloper && (
            <Button variant="outline" onClick={() => setShowGrant(true)}>
              <Users className="w-4 h-4 mr-2" />
              Conceder Acesso
            </Button>
          )}
          <Button onClick={() => setShowCreate(true)} data-testid="create-autopilot-btn">
            <Plus className="w-4 h-4 mr-2" />
            Novo Autopilot
          </Button>
        </div>
      </div>

      {/* Developer Panel */}
      {isDeveloper && status && (
        <Card className="glass-card border-primary/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Shield className="w-5 h-5 text-primary" />
              Painel do Desenvolvedor
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-4 items-center">
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Runner:</span>
                <Badge className={status.runner_active ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20'}>
                  {status.runner_active ? 'Ativo' : 'Parado'}
                </Badge>
                {status.runner_active ? (
                  <Button variant="outline" size="sm" onClick={handleStopRunner}>
                    <Square className="w-4 h-4" />
                  </Button>
                ) : (
                  <Button variant="outline" size="sm" onClick={handleStartRunner}>
                    <Play className="w-4 h-4" />
                  </Button>
                )}
              </div>
              
              <div className="text-sm">
                <span className="text-muted-foreground">Autopilots ativos:</span>{' '}
                <span className="font-bold">{status.autopilots_active}</span>
              </div>
              
              <div className="text-sm">
                <span className="text-muted-foreground">Permissões:</span>{' '}
                <span className="font-bold">{status.permissions_granted}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Content */}
      <Tabs defaultValue="autopilots" className="space-y-4">
        <TabsList>
          <TabsTrigger value="autopilots" className="flex items-center gap-2">
            <Bot className="w-4 h-4" />
            Meus Autopilots
          </TabsTrigger>
          <TabsTrigger value="queue" className="flex items-center gap-2" data-testid="autopilot-queue-tab">
            <Clock className="w-4 h-4" />
            Fila do Agente
          </TabsTrigger>
          {isDeveloper && (
            <TabsTrigger value="permissions" className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              Permissões
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="autopilots">
          {autopilots.length === 0 ? (
            <Card className="glass-card">
              <CardContent className="py-12 text-center">
                <Bot className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">Nenhum Autopilot</h3>
                <p className="text-muted-foreground mb-4">
                  Crie seu primeiro autopilot com um comando em linguagem natural
                </p>
                <Button onClick={() => setShowCreate(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Criar Autopilot
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {autopilots.map(autopilot => (
                <AutopilotCard
                  key={autopilot.id}
                  autopilot={autopilot}
                  accountsMap={Object.fromEntries(vaultAccounts.map((a) => [a.id, a]))}
                  onPause={handlePause}
                  onResume={handleResume}
                  onStop={handleStop}
                  onDelete={handleDelete}
                  onRunNow={handleRunNow}
                  onViewClips={handleViewClips}
                  onEditAccounts={openEditAccounts}
                />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="queue">
          <div className="flex items-center justify-between mb-4">
            <div className="flex flex-wrap gap-2">
              {['pending', 'in_progress', 'completed', 'failed'].map((s) => (
                <Badge key={s} variant="outline" className="text-xs">
                  <TaskStatusDot status={s} />
                  {queueLabel(s)}: {queueCounts[s] || 0}
                </Badge>
              ))}
            </div>
            <Button variant="outline" size="sm" onClick={fetchAgentQueue} disabled={loadingQueue} data-testid="autopilot-queue-refresh">
              <RefreshCw className={`w-4 h-4 mr-1 ${loadingQueue ? 'animate-spin' : ''}`} />
              Atualizar
            </Button>
          </div>

          {agentQueue.length === 0 ? (
            <Card className="glass-card">
              <CardContent className="py-12 text-center">
                <Clock className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">Fila vazia</h3>
                <p className="text-muted-foreground">
                  Quando o autopilot programar ações, elas aparecerão aqui para o Agente executar.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {agentQueue.map((task) => (
                <AgentTaskRow key={task.id} task={task} />
              ))}
            </div>
          )}
        </TabsContent>

        {isDeveloper && (
          <TabsContent value="permissions">

            {permissions.length === 0 ? (
              <Card className="glass-card">
                <CardContent className="py-12 text-center">
                  <Users className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-medium mb-2">Nenhuma Permissão</h3>
                  <p className="text-muted-foreground mb-4">
                    Conceda acesso ao Autopilot para outros usuários
                  </p>
                  <Button onClick={() => setShowGrant(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Conceder Acesso
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-2">
                {permissions.map(perm => (
                  <Card key={perm.id} className="glass-card">
                    <CardContent className="py-4">
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="font-medium">{perm.user_email}</p>
                          <p className="text-sm text-muted-foreground">
                            Max: {perm.max_autopilots} autopilots
                            {perm.expires_at && ` • Expira: ${new Date(perm.expires_at).toLocaleDateString('pt-BR')}`}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className={perm.is_active ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20'}>
                            {perm.is_active ? 'Ativo' : 'Inativo'}
                          </Badge>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRevokePermission(perm.user_email)}
                          >
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        )}
      </Tabs>

      {/* Create Autopilot Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bot className="w-5 h-5" />
              Criar Autopilot
            </DialogTitle>
            <DialogDescription>
              Descreva o que você quer que o autopilot faça em linguagem natural
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Comando</Label>
              <Textarea
                placeholder="Ex: Quero que a conta principal poste apenas clipes de podcast de ciência em pt-BR, postando 10 clipes por dia nos melhores horários"
                value={command}
                onChange={(e) => setCommand(e.target.value)}
                rows={4}
                className="mt-1"
              />
            </div>

            <div className="bg-muted/30 rounded-lg p-3 text-sm">
              <p className="font-medium mb-2">Exemplos de comandos:</p>
              <ul className="space-y-1 text-muted-foreground">
                <li>• &quot;Poste 5 clips de tecnologia por dia no TikTok&quot;</li>
                <li>• &quot;Publique clips de humor em português, 8 por dia, em todas as plataformas&quot;</li>
                <li>• &quot;Quero clips de podcast de negócios, 10 por dia, apenas no YouTube Shorts&quot;</li>
              </ul>
            </div>

            <div>
              <Label className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-green-500" /> Contas do Cofre (opcional)
              </Label>
              <p className="text-xs text-muted-foreground mb-2 mt-1">
                Escolha em quais contas publicar. Se nenhuma for selecionada, o Agente usa qualquer conta salva da plataforma.
              </p>
              <AccountPicker accounts={vaultAccounts} selected={selectedAccountIds} onToggle={toggleCreateAccount} />
            </div>

            <div>
              <Label className="flex items-center gap-2">
                <MessageSquare className="w-4 h-4" /> Instruções personalizadas (opcional)
              </Label>
              <Textarea
                placeholder="Ex: sempre marque @meucanal, use tom divertido, adicione call-to-action no final"
                value={createInstructions}
                onChange={(e) => setCreateInstructions(e.target.value)}
                rows={2}
                className="mt-1"
                data-testid="autopilot-create-instructions"
              />
              <p className="text-xs text-muted-foreground mt-1">Enviadas ao Agente em cada publicação deste autopilot.</p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>
              Cancelar
            </Button>
            <Button onClick={handleCreateAutopilot} disabled={creating}>
              {creating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Criando...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Criar Autopilot
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Accounts Dialog */}
      <Dialog open={showAccounts} onOpenChange={setShowAccounts}>
        <DialogContent className="max-w-md max-h-[88vh] flex flex-col gap-0 p-0">
          <DialogHeader className="p-4 pb-3 border-b border-border shrink-0">
            <DialogTitle className="flex items-center gap-2 text-base">
              <Shield className="w-4 h-4 text-green-500" />
              Configurar Autopilot
            </DialogTitle>
            <DialogDescription className="text-xs">
              Ajuste comando, idioma, contas e instruções. As credenciais nunca são expostas — só o Agente as usa.
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 min-h-0 overflow-y-auto px-4 py-3 space-y-3">
            <div>
              <Label className="flex items-center gap-2 text-sm">
                <Zap className="w-3.5 h-3.5" /> Comando
              </Label>
              <Textarea
                placeholder="Ex: Poste 5 clips de tecnologia por dia no TikTok"
                value={editCommand}
                onChange={(e) => setEditCommand(e.target.value)}
                rows={2}
                className="mt-1 text-sm"
                data-testid="autopilot-edit-command"
              />
              <p className="text-[11px] text-muted-foreground mt-1">Ao mudar o comando, tema, ritmo e buscas são recalculados.</p>
            </div>
            <div>
              <Label className="mb-1 block text-sm">Idioma do conteúdo</Label>
              <Select value={editLanguage} onValueChange={setEditLanguage}>
                <SelectTrigger data-testid="autopilot-edit-language" className="h-9">
                  <SelectValue placeholder="Idioma" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pt-BR">Português (Brasil)</SelectItem>
                  <SelectItem value="en-US">Inglês (English)</SelectItem>
                  <SelectItem value="es-ES">Espanhol (Español)</SelectItem>
                  <SelectItem value="fr-FR">Francês (Français)</SelectItem>
                  <SelectItem value="de-DE">Alemão (Deutsch)</SelectItem>
                  <SelectItem value="it-IT">Italiano</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-medium mb-1.5 block">Contas do Cofre</Label>
              <AccountPicker accounts={vaultAccounts} selected={editAccountIds} onToggle={toggleEditAccount} />
            </div>
            <div>
              <Label className="flex items-center gap-2 text-sm">
                <MessageSquare className="w-3.5 h-3.5" /> Instruções personalizadas
              </Label>
              <Textarea
                placeholder="Ex: sempre marque @meucanal, use tom divertido, CTA no final"
                value={editInstructions}
                onChange={(e) => setEditInstructions(e.target.value)}
                rows={2}
                className="mt-1 text-sm"
                data-testid="autopilot-edit-instructions"
              />
            </div>
          </div>

          <DialogFooter className="p-4 pt-3 border-t border-border shrink-0">
            <Button variant="outline" onClick={() => setShowAccounts(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSaveAccounts} disabled={savingAccounts} data-testid="autopilot-save-accounts-btn">
              {savingAccounts ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Salvando...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Salvar Configurações
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Grant Permission Dialog */}
      <Dialog open={showGrant} onOpenChange={setShowGrant}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Conceder Acesso ao Autopilot
            </DialogTitle>
            <DialogDescription>
              Permita que outro usuário use a funcionalidade de Autopilot
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Email do Usuário</Label>
              <Input
                type="email"
                placeholder="usuario@email.com"
                value={grantForm.email}
                onChange={(e) => setGrantForm(prev => ({ ...prev, email: e.target.value }))}
              />
            </div>

            <div>
              <Label>Máximo de Autopilots</Label>
              <Select 
                value={grantForm.maxAutopilots.toString()}
                onValueChange={(v) => setGrantForm(prev => ({ ...prev, maxAutopilots: parseInt(v) }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[1, 2, 3, 5, 10].map(n => (
                    <SelectItem key={n} value={n.toString()}>{n} autopilots</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Expiração (opcional)</Label>
              <Select 
                value={grantForm.expiresDays}
                onValueChange={(v) => setGrantForm(prev => ({ ...prev, expiresDays: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Nunca expira" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">Nunca expira</SelectItem>
                  <SelectItem value="7">7 dias</SelectItem>
                  <SelectItem value="30">30 dias</SelectItem>
                  <SelectItem value="90">90 dias</SelectItem>
                  <SelectItem value="365">1 ano</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGrant(false)}>
              Cancelar
            </Button>
            <Button onClick={handleGrantPermission}>
              <Shield className="w-4 h-4 mr-2" />
              Conceder Acesso
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Clips Dialog */}
      <Dialog open={showClips} onOpenChange={setShowClips}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Film className="w-5 h-5" />
              Clips do Autopilot
            </DialogTitle>
            <DialogDescription>
              {clips.length} clips processados
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto space-y-3 pr-2">
            {loadingClips ? (
              <div className="space-y-3">
                {[1, 2, 3].map(i => <Skeleton key={i} className="h-32" />)}
              </div>
            ) : clips.length === 0 ? (
              <div className="text-center py-8">
                <Film className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">Nenhum clip processado ainda</p>
                <p className="text-sm text-muted-foreground mt-2">
                  Execute um ciclo para processar clips
                </p>
              </div>
            ) : (
              clips.map(clip => (
                <ClipCard key={clip.id} clip={clip} />
              ))
            )}
          </div>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowClips(false)}>
              Fechar
            </Button>
            <Button 
              onClick={() => handleViewClips(selectedAutopilotId)}
              disabled={loadingClips}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loadingClips ? 'animate-spin' : ''}`} />
              Atualizar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
